import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

import GenerationSection from './components/generation-section/GenerationSection';
import UnsavedModal from '../components/unsaved-modal/UnsavedModal';
import {
  changeGeneratingStatus,
  changeScoutTab,
  resetScoutParams,
  setCollectDataMessages,
  setEditableMessage,
  setIsDirty,
} from '../redux/scout/scoutReducer';
import {
  getScoutFormData,
  getScreenedMessages,
  saveMessageChanges,
  getAllMessages,
  getSelectedMessages,
  getFinalMessages,
  replyScout,
  compressMessages,
  generateMessages,
  generateMore,
  resetSession,
  likeMessage,
  deleteMessage,
  selectMessage,
  getMessageLogs,
  uploadFile,
  clearBoard,
  clearAll,
  exportMessages,
  createMessage,
  reclaimBackup,
  getBackupMessages,
  restoreMessage,
  getMessagesByTab,
} from '../redux/scout/scoutActions';
import MessageFilters from './components/message-filters/MessageFilters';
import useRedirectPrompt from '../hooks/useRedirectPrompt';
import useBindAction from '../hooks/useBindAction';
import MessagesOutput from './components/message-output/MessagesOutput';
import { SCOUT_TABS } from '../constants';
import { clearProjectStatus } from '../redux/global/globalActions';
import useProjectStatusTracker from '../hooks/useProjectStatusTracker';
import CommonPage from '../components/common-page/CommonPage';
import useScoutTabs from './hooks/useScoutTabs';
import CommonTabs from '../components/common-tabs/CommonTabs';
import CommonTabPanel from '../components/common-tabs/CommonTabPanel';

import scss from './Scout.module.scss';

const Scout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const scoutTab = useSelector((state) => state.scout.scoutTab);

  const scoutTabs = useScoutTabs({ isConsumer: false });

  const [isShowGenerationForm, setIsShowGenerationForm] = useState(true);
  const [isShowUnsavedModal, setIsShowUnsavedModal] = useState(false);
  const messagesContent = useSelector((state) => state.scout.messagesContent);
  const { editableMessage, isDirty } = useSelector((state) => state.scout);
  const { triggerAction, bindAction, resetAction } = useBindAction(isDirty, () => handleShowModal());
  const [showPrompt, confirmNavigation, cancelNavigation] = useRedirectPrompt(!!editableMessage.id && isDirty);

  const handleChangeTabs = useCallback(
    (_, newTab) => {
      bindAction(() => dispatch(changeScoutTab(newTab)));
    },
    [dispatch, bindAction],
  );

  const handleShowModal = useCallback(() => {
    setIsShowUnsavedModal(true);
  }, []);

  const handleCancelModal = useCallback(() => {
    setIsShowUnsavedModal(false);
    resetAction(null);

    if (showPrompt) {
      cancelNavigation();
    }
  }, [showPrompt, cancelNavigation, resetAction]);

  const handleDontSave = useCallback(() => {
    setIsShowUnsavedModal(false);
    dispatch(setEditableMessage({ id: null, message: null }));
    dispatch(setIsDirty(false));

    if (triggerAction) {
      triggerAction();
    }

    if (showPrompt) {
      confirmNavigation();
    }
  }, [showPrompt, confirmNavigation, dispatch, triggerAction]);

  const handleSaveModal = useCallback(async () => {
    setIsShowUnsavedModal(false);
    await dispatch(saveMessageChanges({ scoutTab }));

    if (triggerAction) {
      triggerAction();
    }

    if (showPrompt) {
      confirmNavigation();
    }
  }, [showPrompt, confirmNavigation, dispatch, triggerAction, scoutTab]);

  const handleSearchAllMessages = ({ id }) => {
    dispatch(getAllMessages({ id }));
  };

  const handleSearchSelectedMessages = ({ id }) => {
    dispatch(getSelectedMessages({ id }));
  };

  const handleSearchFinalMessages = ({ id }) => {
    dispatch(getFinalMessages({ id }));
  };

  const handleSearchScreenedMessages = ({ id }) => {
    dispatch(getScreenedMessages({ id }));
  };

  const handleSearchBackupMessages = ({ id }) => {
    dispatch(getBackupMessages({ id }));
  };

  const handleCollectResponseData = () => {
    dispatch(setCollectDataMessages(messagesContent));
    navigate(`/project/scout/collect-data/${id}`);
  };

  const MESSAGES_LISTS = {
    [SCOUT_TABS.ALL_MESSAGES]: handleSearchAllMessages,
    [SCOUT_TABS.SELECTED_MESSAGES]: handleSearchSelectedMessages,
    [SCOUT_TABS.FINAL_MESSAGES]: handleSearchFinalMessages,
    [SCOUT_TABS.SCREENED_MESSAGES]: handleSearchScreenedMessages,
    [SCOUT_TABS.BACKUP]: handleSearchBackupMessages,
  };

  const handleResetSession = () => {
    dispatch(clearProjectStatus({ id }));
    dispatch(resetScoutParams());
  };

  const handleUpdateForm = () => {
    dispatch(getScoutFormData({ id }));
    dispatch(getMessagesByTab());
  };

  const handleSetGeneratingStatus = (status) => {
    dispatch(changeGeneratingStatus(status));
  };

  useProjectStatusTracker({
    onChangeStatus: handleSetGeneratingStatus,
    onInitialUpdate: handleUpdateForm,
  });

  useEffect(() => {
    setIsShowUnsavedModal(showPrompt);
  }, [showPrompt]);

  useEffect(() => {
    if (id) {
      dispatch(getScoutFormData({ id }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    return () => {
      handleResetSession();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.addEventListener('beforeunload', handleResetSession);

    return () => {
      window.removeEventListener('beforeunload', handleResetSession);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CommonPage className={scss.pageWrapper}>
      <GenerationSection
        isShowGenerationForm={isShowGenerationForm}
        onShowGenerationForm={setIsShowGenerationForm}
        onReply={replyScout}
        onGenerateMessages={generateMessages}
        onGenerateMore={generateMore}
        onCompressMessages={compressMessages}
        onResetSession={resetSession}
        onFileUpload={uploadFile}
      />

      <CommonTabs tabs={scoutTabs} value={scoutTab} onChange={handleChangeTabs} isFullWidth />

      {Object.entries(MESSAGES_LISTS).map(([tab, searchFunction], index) => (
        <CommonTabPanel value={scoutTab} index={index} key={tab}>
          <div className={scss.outputWrapper}>
            <MessageFilters
              onClearBoard={clearBoard}
              onClearAll={clearAll}
              onReclaim={reclaimBackup}
              onExportMessages={exportMessages}
              onCreateMessage={createMessage}
              onCollectResponseData={handleCollectResponseData}
              onResetSession={resetSession}
            />

            <MessagesOutput
              bindNextAction={bindAction}
              onSearch={searchFunction}
              onSelect={selectMessage}
              onDelete={deleteMessage}
              onLike={likeMessage}
              onRestore={restoreMessage}
              onSaveMessage={saveMessageChanges}
              getMessageLogs={getMessageLogs}
              maxHeight={isShowGenerationForm ? 'calc(100vh - 536px)' : 'calc(100vh - 356px)'}
            />
          </div>
        </CommonTabPanel>
      ))}

      <UnsavedModal
        isOpen={isShowUnsavedModal}
        onCancel={handleCancelModal}
        onClose={handleCancelModal}
        onDontSave={handleDontSave}
        onSave={handleSaveModal}
      />
    </CommonPage>
  );
};

export default memo(Scout);
